<template>
  <div class="glass-select" :class="{ 'glass-select--error': hasError, 'glass-select--disabled': disabled }">
    <label v-if="label" :for="selectId" class="glass-select__label">
      {{ label }}
      <span v-if="required" class="glass-select__required">*</span>
    </label>
    
    <div class="glass-select__wrapper">
      <span v-if="icon" class="glass-select__icon">
        <i :class="icon"></i>
      </span>
      
      <select
        :id="selectId"
        :value="modelValue"
        :disabled="disabled"
        class="glass-select__field"
        :class="{ 'glass-select__field--has-icon': icon }"
        @change="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option 
          v-for="option in normalizedOptions" 
          :key="option.value" 
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      
      <span class="glass-select__arrow">
        <i class="fas fa-chevron-down"></i>
      </span>
    </div>
    
    <transition name="slide-fade">
      <span v-if="hasError && errorMessage" class="glass-select__error">
        {{ errorMessage }}
      </span>
    </transition>
  </div>
</template>

<script>
let selectCounter = 0;

export default {
  name: 'GlassSelect',
  emits: ['update:modelValue', 'blur'],
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Sélectionner...'
    },
    icon: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: null
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    }
  },
  data() {
    return {
      selectId: `glass-select-${++selectCounter}`
    };
  },
  computed: {
    normalizedOptions() {
      return this.options.map(option => {
        if (typeof option === 'object') {
          return {
            value: option[this.valueKey],
            label: option[this.labelKey]
          };
        }
        return { value: option, label: option };
      });
    }
  }
};
</script>

<style scoped>
.glass-select {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.glass-select__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #424242;
}

.glass-select__required {
  color: #c3423f;
  margin-left: 2px;
}

.glass-select__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.glass-select__field {
  width: 100%;
  padding: 0.875rem 3rem 0.875rem 1rem;
  font-family: inherit;
  font-size: 1rem;
  color: #424242;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  transition: all 0.3s ease;
}

.glass-select__field:focus {
  border-color: var(--theme-primary, rgba(89, 169, 106, 0.6));
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 0 4px rgba(var(--theme-primary-rgb, 89, 169, 106), 0.1);
}

.glass-select__field--has-icon {
  padding-left: 3rem;
}

/* Icon */
.glass-select__icon {
  position: absolute;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 100%;
  color: rgba(66, 66, 66, 0.5);
  pointer-events: none;
}

/* Arrow */
.glass-select__arrow {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 100%;
  color: rgba(66, 66, 66, 0.5);
  pointer-events: none;
  transition: transform 0.2s ease;
}

.glass-select__field:focus + .glass-select__arrow {
  color: var(--theme-primary, #59a96a);
}

/* Error state */
.glass-select--error .glass-select__field {
  border-color: rgba(195, 66, 63, 0.6);
  background: rgba(195, 66, 63, 0.05);
}

.glass-select--error .glass-select__field:focus {
  box-shadow: 0 0 0 4px rgba(195, 66, 63, 0.1);
}

.glass-select__error {
  font-size: 0.8125rem;
  color: #c3423f;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.glass-select__error::before {
  content: '⚠';
  font-size: 0.75rem;
}

/* Disabled state */
.glass-select--disabled .glass-select__field {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(0, 0, 0, 0.05);
}

/* Animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
